import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { Loader2, AlertCircle, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

/**
 * DocxViewer - Renders .docx files inline using docx-preview library
 *
 * @param {string} url - Signed URL to the .docx file
 * @param {string} className - Optional className for the container
 * @param {string} height - Height of the viewer container (default: '300px')
 */
export default function DocxViewer({ url, className = '', height = '300px' }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(60); // Start at 60% to fit typical A4 documents

  const handleZoomChange = (value) => setZoom(value[0]);
  const zoomIn = () => setZoom(prev => Math.min(prev + 10, 150));
  const zoomOut = () => setZoom(prev => Math.max(prev - 10, 30));
  const resetZoom = () => setZoom(60);

  useEffect(() => {
    if (!url || !containerRef.current) return;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch the document as a blob
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const blob = await response.blob();

        // Clear any previous content
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // Render the docx file
        await renderAsync(blob, containerRef.current, null, {
          className: 'docx-wrapper',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        setLoading(false);
      } catch (err) {
        logger.error('Error rendering document:', err);
        setError(err.message || 'Nie udało się załadować dokumentu');
        setLoading(false);
      }
    };

    loadDocument();
  }, [url]);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg ${className}`}>
        <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-red-600 text-sm text-center">{error}</p>
        <p className="text-slate-500 text-xs mt-2">
          Spróbuj odświeżyć stronę lub skontaktuj się z administratorem
        </p>
      </div>
    );
  }

  const zoomDecimal = zoom / 100;

  return (
    <div className={`relative flex flex-col ${className}`} style={{ height }}>
      {/* Zoom controls - always visible at top */}
      <div className="flex-shrink-0 flex items-center gap-3 bg-slate-100 border-b px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={zoomOut}
          disabled={zoom <= 30}
          className="h-7 w-7 p-0"
          title="Pomniejsz"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>

        <div className="flex-1 flex items-center gap-2">
          <Slider
            value={[zoom]}
            onValueChange={handleZoomChange}
            min={30}
            max={150}
            step={5}
            className="flex-1"
          />
          <span className="text-xs text-slate-600 w-10 text-right font-medium">
            {zoom}%
          </span>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={resetZoom}
          className="h-7 px-2 text-xs"
          title="Dopasuj do szerokości"
        >
          <Maximize2 className="w-3 h-3 mr-1" />
          Dopasuj
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={zoomIn}
          disabled={zoom >= 150}
          className="h-7 w-7 p-0"
          title="Powiększ"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
      </div>

      {/* Document container with fixed height and always-visible scrollbars */}
      <div
        className="docx-scroll-container flex-1 relative"
        style={{
          overflow: 'scroll', // Always show scrollbars
          minHeight: 0, // Important for flex child to respect parent height
        }}
      >
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
            <p className="text-slate-600 text-sm">Ładowanie dokumentu...</p>
          </div>
        )}

        <div
          ref={containerRef}
          className="docx-container"
          style={{
            transform: `scale(${zoomDecimal})`,
            transformOrigin: 'top left',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            width: `${100 / zoomDecimal}%`, // Compensate for scale to enable proper scrolling
          }}
        />
      </div>

      <style>{`
        .docx-scroll-container {
          background: #f8fafc;
        }
        /* Always show scrollbars on webkit browsers */
        .docx-scroll-container::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        .docx-scroll-container::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 5px;
        }
        .docx-scroll-container::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 5px;
        }
        .docx-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
        .docx-scroll-container::-webkit-scrollbar-corner {
          background: #e2e8f0;
        }
        .docx-container {
          background: white;
        }
        .docx-container .docx-wrapper {
          background: white;
          padding: 0;
        }
        .docx-container .docx-wrapper > section.docx {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
          margin: 8px auto !important;
          padding: 20px !important;
          min-height: auto !important;
          background: white !important;
        }
        .docx-container .docx-wrapper > section.docx article {
          font-size: 14px !important;
          line-height: 1.6 !important;
        }
        .docx-container .docx-wrapper > section.docx p {
          margin: 0 0 10px 0 !important;
        }
        .docx-container .docx-wrapper > section.docx table {
          font-size: 13px !important;
          border-collapse: collapse !important;
        }
        .docx-container .docx-wrapper > section.docx table td,
        .docx-container .docx-wrapper > section.docx table th {
          padding: 4px 8px !important;
        }
        .docx-container .docx-wrapper > section.docx h1,
        .docx-container .docx-wrapper > section.docx h2,
        .docx-container .docx-wrapper > section.docx h3 {
          margin: 16px 0 8px 0 !important;
        }
        .docx-container .docx-wrapper > section.docx ul,
        .docx-container .docx-wrapper > section.docx ol {
          padding-left: 24px !important;
          margin: 8px 0 !important;
        }
      `}</style>
    </div>
  );
}
